import { t } from 'ttag'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { useContext } from 'react'

import {
  EducationIcon,
  JobsIcon,
  CollegeIcon,
  WorkBasedLearningIcon
} from '@h2/components/icons'
import { WHITE, SO60S, PB90T, PG } from '@h2/components/colors'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Link } from '@h2/common/components/link'
import { Skeleton } from '@h2/components/loading/skeleton'
import { Flex } from '@h2/components/layout/flex'
import { Globals } from '@h2/globals'

const MoreItemsContainer = styled.div`
  padding: 0 5px;
`
const SectionBox = styled.div`
  border: 1px solid ${PB90T};
  background-color: ${WHITE};
  width: 100%;
  text-align: center;
  padding: 15px 5px;
  border-radius: 8px;
`
const WhiteCircle = styled.div`
  height: 50px;
  text-align: center;
`
const RecommendationCard = styled.div`
  border-radius: 5px;
  background-color: white;
  min-height: 150px;
  width: 100%;
  margin-top: 5px;
  margin-bottom: 15px;
  padding: 10px;
`
const Holder = styled.div`
  margin-bottom: 15px;
`
const CardContent = ({ item, nextStepUrl, resultCount, textFn, viewAllText }) => {
  if (!item || item.length === 0) {
    return (
      <div className="clear">
        <RecommendationCard>
          <Text color={SO60S} size="medium" align="center">
            {t`There are no related items to show at this time`}
          </Text>
        </RecommendationCard>
      </div>
    )
  }

  const resultCountFormatted = resultCount?.toLocaleString() ?? 'n/a'

  return (
    <MoreItemsContainer>
      <Text align="center">{textFn(resultCountFormatted)}</Text>
      {nextStepUrl && (
        <Link href={nextStepUrl} variant="anchor" color={PG} align="center" weight="bold">
          {viewAllText}
        </Link>
      )}
    </MoreItemsContainer>
  )
}
const HowToPursue = ({
  title,
  recommendations,
  includeInstitutions = true,
  includeTraining = true
}) => {
  const { hasLocalOpportunities } = useContext(Globals)
  const contentOrLoader = sectionKey => {
    if (!recommendations) {
      return <Skeleton width="100%" height="100px" />
    }

    const lookupNextStep = () => {
      switch (sectionKey) {
        case 'educationTraining':
          return {
            link: recommendations.moreEducationLink,
            count: recommendations.totalEducationLink,
            textFn: count => t`${count} educational opportunities`,
            viewAllText: t`View Education & Training`
          }
        case 'institutions':
          return {
            link: recommendations.moreInstitutionsLink,
            count: recommendations.totalInstitutionsLink,
            textFn: count => t`${count} institutions with related majors`,
            viewAllText: t`View Institutions`
          }
        case 'jobOpenings':
          return {
            link: recommendations.moreJobOpeningsLink,
            count: recommendations.totalJobsLink,
            textFn: count => t`${count} job openings in this occupation`,
            viewAllText: t`View Jobs`
          }
        case 'occupations':
          return {
            link: recommendations.moreOccupationsLink,
            count: recommendations.totalOccupationsLink,
            textFn: count => t`${count} items`,
            viewAllText: t`View All`
          }
        case 'otherOpportunities':
          return {
            link: recommendations.moreOpportunitiesLink,
            count: recommendations.totalOpportunitiesLink,
            textFn: count => t`${count} items`,
            viewAllText: t`View All`
          }
        default:
          return null
      }
    }
    const next = lookupNextStep()

    return (
      <CardContent
        item={recommendations[sectionKey]}
        nextStepUrl={next.link}
        resultCount={next.count}
        viewAllText={next.viewAllText}
        textFn={next.textFn}
      />
    )
  }

  return (
    <Holder>
      {title && (
        <Heading style={{ padding: '0 15px' }} level="4" fontSize="30px">
          {title}
        </Heading>
      )}
      <Flex gap="15px">
        <SectionBox>
          <WhiteCircle>
            <JobsIcon size="large" />
          </WhiteCircle>

          {contentOrLoader('jobOpenings')}
        </SectionBox>
        {includeTraining && (
          <SectionBox>
            <WhiteCircle>
              <EducationIcon size="large" />
            </WhiteCircle>
            {contentOrLoader('educationTraining')}
          </SectionBox>
        )}
        {includeInstitutions && (
          <SectionBox>
            <WhiteCircle>
              <CollegeIcon size="large" />
            </WhiteCircle>
            {contentOrLoader('institutions')}
          </SectionBox>
        )}
        {hasLocalOpportunities && (
          <SectionBox>
            <WhiteCircle>
              <WorkBasedLearningIcon size="large" />
            </WhiteCircle>
            <MoreItemsContainer>
              <Text align="center">{t`Inform your choices`}</Text>
              <Link
                href="/opportunities"
                variant="anchor"
                color={PG}
                align="center"
                weight="bold">
                {t`View Local Opportunities`}
              </Link>
            </MoreItemsContainer>
          </SectionBox>
        )}
      </Flex>
    </Holder>
  )
}

HowToPursue.propTypes = {
  title: PropTypes.string,
  recommendations: PropTypes.object.isRequired,
  user: PropTypes.object,
  objectId: PropTypes.string,
  objectType: PropTypes.string,
  includeInstitutions: PropTypes.bool,
  includeTraining: PropTypes.bool
}

export { HowToPursue }
